/**
 * Shared primitives used by every DTO → view-model mapper.
 *
 * These live here so each resource-specific mapper (patient, feature,
 * diagnosis, variant, …) doesn't re-implement the same nullable-string
 * normalisation and enum narrowing. Extending either helper changes the
 * behaviour uniformly and makes new mappers cheaper to write.
 *
 * The leading underscore in the filename signals "internal to the
 * mappers directory" — nothing outside `src/api/mappers/` should import
 * from here, and the public `index.ts` barrel does not re-export.
 */

/**
 * Normalises an optional server-side string into a view-model-friendly
 * ``string | null``. Whitespace-only values collapse to ``null`` so the
 * UI only branches on "truly present" data.
 *
 * Accepts ``unknown`` defensively: when the server returns the wrong type
 * (e.g. an epoch number where the contract said ``string``), we drop it
 * to ``null`` rather than crash the mapper. This used to throw "value.trim
 * is not a function," which surfaced in React Query as a generic
 * non-``ApiError`` and showed "Code: unknown" in the UI.
 */
export function nullableString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Narrows an unknown string into a closed union member. Any value not in
 * ``known`` — including ``null`` / ``undefined`` — collapses to
 * ``fallback``. The two overloads preserve return-type inference so
 * ``null`` fallbacks widen to ``T | null`` and concrete fallbacks stay
 * as ``T``.
 *
 * This centralises the "defend against server-side enum drift" rule so
 * new mappers don't have to reinvent it.
 */
export function narrowEnum<T extends string>(
  value: string | null | undefined,
  known: ReadonlySet<T>,
  fallback: T,
): T;
export function narrowEnum<T extends string>(
  value: string | null | undefined,
  known: ReadonlySet<T>,
  fallback: null,
): T | null;
export function narrowEnum<T extends string>(
  value: string | null | undefined,
  known: ReadonlySet<T>,
  fallback: T | null,
): T | null {
  if (value === null || value === undefined) return fallback;
  for (const k of known) {
    if (k === value) {
      return k;
    }
  }
  return fallback;
}
